import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiBriefcase, FiAlertCircle } from "react-icons/fi";
import { useDistanceCalculation } from "../../hooks/useDistanceCalculation";
import {
  listEnterprisesLite,
  listPackagesLite,
  listCities,
  calculateFarePrice,
  uploadDutySlip,
  uploadParkingSlip
} from "../../api/bookings";
import { getEnterpriseById } from "../../api/enterprises";
import { getVehicleCategories } from "../../api/vehicles";
import { toPickupAtIso, localToday, isPickupInPast } from "../../utils/pickupTime";
import { extractErrorMessage, TOAST_ERR } from "../../utils/apiError";
import { toast } from "react-toastify";

import PassengerSection from "./booking-form/PassengerSection";
import AssignmentSection from "./booking-form/AssignmentSection";
import PickupSection from "./booking-form/PickupSection";
import DropSection from "./booking-form/DropSection";
import PricingSection from "./booking-form/PricingSection";
import { TabButton, Card, Label, S, FieldError } from "./booking-form/fields";

const empty = {
  enterpriseId: "",
  enterpriseName: "",
  paymentMethod: "",

  paxName: "",
  paxPhone: "",
  email: "",
  gender: "",
  mobile2: "",
  emailAlt: "",

  city: "",
  dutyType: "",
  outstationType: "",
  vehicleClass: "",
  carRequiredOn: "",
  carRequiredTime: "",

  pickupMode: "AIRPORT",
  pickupAirportName: "",
  pickupAddress: "",
  pickupGoogleAddr: "",
  pickupLat: "",
  pickupLng: "",
  pickupFlightNo: "",

  pickupPlaceId: "",
  pickupFormattedAddress: "",
  pickupCity: "",
  pickupState: "",
  pickupCountry: "",
  pickupPostalCode: "",
  pickupTypes: [],

  dropMode: "POINT",
  dropAirportName: "",
  dropAddress: "",
  dropGoogleAddr: "",
  dropLat: "",
  dropLng: "",
  dropCity: "",
  dropFlightNo: "",

  dropPlaceId: "",
  dropFormattedAddress: "",
  dropState: "",
  dropCountry: "",
  dropPostalCode: "",
  dropTypes: [],

  terminateAt: "",
  packageId: "",
  packageLabel: "",
  fare: "",
  notes: "",
  distanceKm: "",
  durationHr: "",
};

// Everything taken from a Google place for one end of the trip. `pickupCity`
// is only Google's locality for the pickup; the booking's city is `city`.
const blankPlace = (side) => ({
  [`${side}GoogleAddr`]: "",
  [`${side}Lat`]: "",
  [`${side}Lng`]: "",
  [`${side}PlaceId`]: "",
  [`${side}FormattedAddress`]: "",
  [`${side}City`]: "",
  [`${side}State`]: "",
  [`${side}Country`]: "",
  [`${side}PostalCode`]: "",
  [`${side}Types`]: [],
});

const isSet = (x) => x !== "" && x != null;
const hasPin = (x, side) => isSet(x[`${side}Lat`]) && isSet(x[`${side}Lng`]);

// In form order: drives the error list and which field gets focus first.
const FIELD_LABELS = {
  enterpriseId: "Booking entity",
  paxPhone: "Mobile No",
  paxName: "Passenger name",
  email: "Email ID",
  mobile2: "Mobile 2",
  emailAlt: "Alternate email",
  city: "City",
  dutyType: "Duty type",
  outstationType: "Outstation type",
  vehicleClass: "Vehicle category",
  carRequiredOn: "Pickup date",
  carRequiredTime: "Pickup time",
  packageId: "Package",
  pickupAirportName: "Pickup airport",
  pickupAddress: "Pickup address",
  dropAirportName: "Drop airport",
  dropAddress: "Drop address",
  distanceKm: "Distance",
  durationHr: "Duration",
  fare: "Fare",
  paymentMethod: "Payment method",
};

const orderedErrorKeys = (errs) => [
  ...Object.keys(FIELD_LABELS).filter((k) => errs[k]),
  ...Object.keys(errs).filter((k) => errs[k] && !FIELD_LABELS[k]),
];

// Server validation paths -> form fields, so a rejected value is flagged in place.
const SERVER_FIELDS = {
  enterprise: "enterpriseId",
  "pax.name": "paxName",
  "pax.phone": "paxPhone",
  "pax.email": "email",
  mobile2: "mobile2",
  emailAlt: "emailAlt",
  pickupCity: "city",
  dutyType: "dutyType",
  outstationType: "outstationType",
  "pricingSnapshot.vehicleCategory": "vehicleClass",
  pickupAt: "carRequiredOn",
  package: "packageId",
  distanceKm: "distanceKm",
  durationHr: "durationHr",
  "pricingSnapshot.transferFlatRate": "fare",
  "charges.base": "fare",
  paymentMethod: "paymentMethod",
};

const serverFieldErrors = (list, v) => {
  const out = {};
  for (const { field, message } of Array.isArray(list) ? list : []) {
    let key = SERVER_FIELDS[field];
    if (field === "pickupAddress") key = v.pickupMode === "AIRPORT" ? "pickupAirportName" : "pickupAddress";
    if (field === "dropAddress") key = v.dropMode === "AIRPORT" ? "dropAirportName" : "dropAddress";
    if (key && message && !out[key]) out[key] = message;
  }
  return out;
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "98765 43210", "+91-9876543210" and "09876543210" all become "9876543210".
const normalizePhone = (value) => {
  let digits = String(value ?? "").replace(/[\s\-().]/g, "");
  if (/^\+?91\d{10}$/.test(digits)) digits = digits.replace(/^\+?91/, "");
  else if (/^0\d{10}$/.test(digits)) digits = digits.slice(1);
  return digits;
};

const norm = (s) => String(s ?? "").trim().toLowerCase();
const findOption = (options, value, field = "value") =>
  (norm(value) && options.find((o) => norm(o[field]) === norm(value))) || null;

// Google names some cities differently from the City master.
const CITY_ALIASES = { "new delhi": "delhi", gurugram: "gurgaon", bengaluru: "bangalore", mysuru: "mysore" };
const findCityOption = (options, name) => {
  const key = norm(name);
  if (!key) return null;
  const names = [key, CITY_ALIASES[key], ...Object.keys(CITY_ALIASES).filter((k) => CITY_ALIASES[k] === key)];
  return options.find((o) => names.includes(norm(o.label))) || null;
};

const manualBreakdown = (fare, v, activeTab, pkgs) => ({
  totalFare: fare,
  breakdown: {
    baseFare: fare,
    extraKm: 0,
    extraKmCharges: 0,
    extraHr: 0,
    extraHrCharges: 0,
    includedKm: 0,
    includedHr: 0,
    extraPerKm: 0,
    extraPerHr: 0,
    notes: ""
  },
  package: {
    name: v.packageLabel || "Manual",
    code: pkgs.find((p) => String(p.value) === String(v.packageId))?.code || ""
  },
  enterprise: { name: activeTab === "individual" ? "Individual" : (v.enterpriseName || "Manual") },
  vehicle: { category: v.vehicleClass || "", model: "" }
});

const planBreakdown = (data, total, roundTrip, fallback) => ({
  totalFare: total,
  roundTrip,
  breakdown: {
    baseFare: data.breakdown?.baseFare ?? data.price,
    extraKm: 0,
    extraKmCharges: 0,
    extraHr: 0,
    extraHrCharges: 0,
    includedKm: data.breakdown?.includedKm || 0,
    includedHr: data.breakdown?.includedHr || 0,
    extraPerKm: data.breakdown?.extraPerKm || 0,
    extraPerHr: data.breakdown?.extraPerHr || 0,
    notes: data.breakdown?.notes || ""
  },
  package: {
    name: data.package?.displayName || fallback.packageLabel || "Manual",
    code: data.package?.code || ""
  },
  enterprise: { name: data.enterprise?.name || fallback.enterpriseName },
  vehicle: {
    category: data.vehicleCategory?.category || fallback.vehicleClass || "",
    model: data.vehicleCategory?.vehicleModel || ""
  }
});

export default function BookingCreateForm({ initial, onCancel, onSubmit, readOnly = false }) {
  const [v, setV] = useState(() => (initial ? { ...empty, ...initial } : empty));
  const [activeTab, setActiveTab] = useState(() => (initial ? (initial.enterpriseId ? "enterprise" : "individual") : "enterprise"));

  const [ents, setEnts] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lookupErrors, setLookupErrors] = useState([]);

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [placeIssues, setPlaceIssues] = useState({ pickup: "", drop: "" });
  const [routeEstimate, setRouteEstimate] = useState(null);
  const [fareBreakdown, setFareBreakdown] = useState(null);
  const [fareStatus, setFareStatus] = useState({ state: "idle", message: "" });

  // A new booking fills distance and fare in as it goes. An edited booking
  // keeps its saved values until the user changes something they depend on,
  // so opening it never silently re-prices it.
  const autoDistance = useRef(!initial);
  const autoFare = useRef(!initial);
  // True while the fare on the form came from a fare plan. A fare the user
  // typed (or an edited booking's saved fare) is never replaced by a lookup;
  // the plan fare is offered next to it instead.
  const fareIsAuto = useRef(false);
  const currentFare = useRef(v.fare);
  currentFare.current = v.fare;
  const fareRequest = useRef(0);
  const enterpriseRequest = useRef(0);
  const enterpriseFilled = useRef({});
  const rootRef = useRef(null);

  const {
    calculateDistanceWithCoords,
    loading: distanceLoading,
    error: distanceError,
  } = useDistanceCalculation({
    useHaversine: true,
    useGoogleAPI: false
  });

  const isOutstation = v.dutyType === "OUTSTATION";
  const isRoundTrip = isOutstation && v.outstationType === "ROUND_WAY";

  useEffect(() => {
    setV(initial ? { ...empty, ...initial } : empty);
    setActiveTab(initial ? (initial.enterpriseId ? "enterprise" : "individual") : "enterprise");
    autoDistance.current = !initial;
    autoFare.current = !initial;
    fareIsAuto.current = false;
    enterpriseFilled.current = {};
    setErrors({});
    setServerError("");
    setRouteEstimate(null);
    setFareStatus({ state: "idle", message: "" });
    setPlaceIssues({ pickup: "", drop: "" });
  }, [initial]);

  useEffect(() => {
    if (initial && initial.charges && (initial.charges.base || initial.charges.total)) {
      const charges = initial.charges;
      setFareBreakdown({
        totalFare: charges.total || charges.base || 0,
        breakdown: {
          baseFare: charges.base || 0,
          extraKm: charges.extraKm || 0,
          extraKmCharges: charges.extraKmCharges || 0,
          extraHr: charges.extraHr || 0,
          extraHrCharges: charges.extraHrCharges || 0,
          includedKm: 0,
          includedHr: 0
        },
        package: {
          name: initial.packageLabel || "Manual",
          code: ""
        },
        enterprise: {
          name: initial.enterpriseName || "Manual"
        }
      });
    } else if (initial && initial.fare && initial.fare !== "") {
      setFareBreakdown(manualBreakdown(Number(initial.fare), initial, initial.enterpriseId ? "enterprise" : "individual", []));
    } else {
      setFareBreakdown(null);
    }
  }, [initial]);

  // Each list loads on its own: one failing (e.g. no permission for cities)
  // must not blank every other dropdown, and the reason is shown on the form.
  useEffect(() => {
    let cancelled = false;
    const sources = [
      ["enterprises", listEnterprisesLite, setEnts],
      ["packages", listPackagesLite, setPkgs],
      ["cities", listCities, setCities],
      ["vehicle categories", getVehicleCategories, setCategories],
    ];
    Promise.allSettled(sources.map(([, load]) => load())).then((results) => {
      if (cancelled) return;
      const failures = [];
      results.forEach((result, i) => {
        const [label, , store] = sources[i];
        if (result.status === "fulfilled") {
          store((Array.isArray(result.value) ? result.value : []).filter((o) => o && o.value));
        } else {
          failures.push(`Couldn't load ${label}: ${extractErrorMessage(result.reason, "the request failed.")}`);
        }
      });
      setLookupErrors(failures);
    });
    return () => { cancelled = true; };
  }, []);

  // Saved bookings and enterprise pricing may spell a city or category with
  // different casing than the masters; line them up with the dropdown values.
  useEffect(() => {
    if (!cities.length) return;
    setV((x) => {
      const match = findOption(cities, x.city, "label");
      return match && match.label !== x.city ? { ...x, city: match.label } : x;
    });
  }, [cities]);

  useEffect(() => {
    if (!categories.length) return;
    setV((x) => {
      const match = findOption(categories, x.vehicleClass);
      return match && match.value !== x.vehicleClass ? { ...x, vehicleClass: match.value } : x;
    });
  }, [categories]);

  // Keep a saved value visible even when it is no longer in its master list.
  const cityOptions = useMemo(() => {
    if (!v.city || findOption(cities, v.city, "label")) return cities;
    return [...cities, { value: `saved:${v.city}`, label: v.city }];
  }, [cities, v.city]);

  const categoryOptions = useMemo(() => {
    if (!v.vehicleClass || findOption(categories, v.vehicleClass)) return categories;
    const label = categories.length ? `${v.vehicleClass} (not in Vehicle Categories)` : v.vehicleClass;
    return [...categories, { value: v.vehicleClass, label }];
  }, [categories, v.vehicleClass]);

  const clearErrors = useCallback((...keys) => {
    setErrors((e) => {
      if (!keys.some((k) => e[k])) return e;
      const next = { ...e };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  const set = (k) => (e) => {
    const value = e.target.value;
    if (["city", "vehicleClass", "dutyType", "outstationType"].includes(k)) autoFare.current = true;
    if (["dutyType", "outstationType"].includes(k)) autoDistance.current = true;

    const related = { dutyType: ["outstationType"], carRequiredOn: ["carRequiredTime"], carRequiredTime: ["carRequiredOn"] };
    clearErrors(k, ...(related[k] || []));

    if (k === "fare") {
      fareIsAuto.current = false;
      const amount = Number(value);
      setFareBreakdown(value !== "" && amount > 0 ? manualBreakdown(amount, v, activeTab, pkgs) : null);
      setFareStatus((s) => (s.state === "error" ? { state: "idle", message: "" } : s));
    }

    setV((x) => {
      const next = { ...x, [k]: value };
      if (k === "dutyType" && value !== "OUTSTATION") next.outstationType = "";
      return next;
    });
  };

  const switchTab = (tab) => {
    // The booking type decides billing, so it is fixed once the booking exists.
    if (initial || tab === activeTab) return;
    autoFare.current = true;
    setActiveTab(tab);
    clearErrors("enterpriseId", "paymentMethod");
  };

  const onModeChange = (side, mode) => {
    if (v[`${side}Mode`] === mode) return;
    autoDistance.current = true;
    setRouteEstimate(null);
    setPlaceIssues((p) => ({ ...p, [side]: "" }));
    clearErrors(`${side}Address`, `${side}AirportName`);
    setV((x) => ({
      ...x,
      ...blankPlace(side),
      [`${side}Mode`]: mode,
      ...(mode === "AIRPORT"
        ? { [`${side}Address`]: "" }
        : { [`${side}AirportName`]: "", [`${side}FlightNo`]: "" }),
      distanceKm: "",
      durationHr: "",
    }));
  };

  // Typing replaces the text, so any previously pinned location no longer
  // belongs to it: drop the coordinates rather than keep a stale point.
  const onPlaceTyping = (side, text) => {
    if ((v[`${side}GoogleAddr`] || "") === text) return;
    const wasPinned = hasPin(v, side);
    if (wasPinned) {
      autoDistance.current = true;
      setRouteEstimate(null);
    }
    setPlaceIssues((p) => (p[side] ? { ...p, [side]: "" } : p));
    setV((x) => ({
      ...x,
      ...blankPlace(side),
      [`${side}GoogleAddr`]: text,
      ...(wasPinned ? { distanceKm: "", durationHr: "" } : {}),
    }));
  };

  const onPlaceSelect = (side, place) => {
    autoDistance.current = true;
    setPlaceIssues((p) => ({ ...p, [side]: place?.detailsError || "" }));

    if (!place) {
      const wasPinned = hasPin(v, side);
      setRouteEstimate(null);
      setV((x) => ({ ...x, ...blankPlace(side), ...(wasPinned ? { distanceKm: "", durationHr: "" } : {}) }));
      return;
    }

    const cityMatch = side === "pickup" && !v.city ? findCityOption(cities, place.city) : null;
    if (cityMatch) autoFare.current = true;
    clearErrors(`${side}Address`, `${side}AirportName`, ...(cityMatch ? ["city"] : []));

    setV((x) => {
      const next = {
        ...x,
        ...blankPlace(side),
        [`${side}GoogleAddr`]: place.formattedAddress || "",
        [`${side}Lat`]: place.lat ?? "",
        [`${side}Lng`]: place.lng ?? "",
        [`${side}PlaceId`]: place.placeId || "",
        [`${side}FormattedAddress`]: place.formattedAddress || "",
        [`${side}City`]: place.city || "",
        [`${side}State`]: place.state || "",
        [`${side}Country`]: place.country || "",
        [`${side}PostalCode`]: place.postalCode || "",
        [`${side}Types`]: place.types || [],
      };
      // Save retyping: the required address / airport field takes the map's
      // full address or place name when it is still empty.
      if (x[`${side}Mode`] === "POINT" && !String(x[`${side}Address`] || "").trim()) {
        next[`${side}Address`] = place.formattedAddress || "";
      }
      if (x[`${side}Mode`] === "AIRPORT" && !String(x[`${side}AirportName`] || "").trim()) {
        next[`${side}AirportName`] = place.name || "";
      }
      if (cityMatch && !x.city) next.city = cityMatch.label;
      return next;
    });
  };

  const pinKey = [v.pickupLat, v.pickupLng, v.dropLat, v.dropLng].join("|");
  const bothPinned = hasPin(v, "pickup") && hasPin(v, "drop");

  const estimateRoute = useCallback(async (x) => {
    const result = await calculateDistanceWithCoords(
      x.pickupGoogleAddr || "Pickup Location",
      x.dropGoogleAddr || "Drop Location",
      Number(x.pickupLat),
      Number(x.pickupLng),
      Number(x.dropLat),
      Number(x.dropLng)
    );
    return result ? { km: result.distance.value, minutes: result.duration?.value || 0 } : null;
  }, [calculateDistanceWithCoords]);

  useEffect(() => {
    if (!bothPinned) {
      setRouteEstimate(null);
      return;
    }
    let cancelled = false;
    estimateRoute(v).then((estimate) => {
      if (!cancelled) setRouteEstimate(estimate);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinKey, estimateRoute]);

  // One place turns the one-way estimate into the form's distance and
  // duration, doubling both for a round trip.
  useEffect(() => {
    if (!routeEstimate || !autoDistance.current) return;
    const factor = isRoundTrip ? 2 : 1;
    setV((x) => ({
      ...x,
      distanceKm: (routeEstimate.km * factor).toFixed(1),
      durationHr: ((routeEstimate.minutes / 60) * factor).toFixed(1),
    }));
  }, [routeEstimate, isRoundTrip]);

  const recalculateDistance = async () => {
    if (!bothPinned) return;
    autoDistance.current = true;
    const estimate = await estimateRoute(v);
    if (estimate) setRouteEstimate(estimate);
  };

  // Fare comes from the enterprise's pricing (Enterprise tab) or the city's
  // B2C sheet (Individual tab). It only depends on these inputs, not on the
  // distance, and a slow answer for older inputs is ignored.
  useEffect(() => {
    if (readOnly || !autoFare.current) return;

    const enterpriseId = activeTab === "enterprise" ? v.enterpriseId : "";
    if ((activeTab === "enterprise" && !enterpriseId) || !v.packageId || !v.city || !v.vehicleClass) {
      fareRequest.current += 1;
      setFareStatus({ state: "idle", message: "" });
      return;
    }

    const request = ++fareRequest.current;
    const context = { packageLabel: v.packageLabel, enterpriseName: activeTab === "individual" ? "Individual" : v.enterpriseName, vehicleClass: v.vehicleClass };
    const timer = setTimeout(async () => {
      setFareStatus({ state: "loading", message: "" });
      try {
        const resp = await calculateFarePrice({
          packageId: v.packageId,
          city: v.city,
          vehicleCategory: v.vehicleClass,
          enterpriseId: enterpriseId || undefined
        });
        if (request !== fareRequest.current) return;

        const price = resp?.data?.price;
        if (typeof price !== "number") throw new Error(resp?.message || "The fare plan did not return a price.");

        const total = isRoundTrip ? price * 2 : price;
        const breakdown = planBreakdown(resp.data, total, isRoundTrip, context);
        if (!fareIsAuto.current && isSet(currentFare.current) && Number(currentFare.current) !== total) {
          setFareStatus({ state: "suggestion", message: "", total, breakdown });
          return;
        }
        fareIsAuto.current = true;
        setV((x) => ({ ...x, fare: String(total) }));
        setFareBreakdown(breakdown);
        setFareStatus({ state: "ok", message: "" });
        clearErrors("fare");
      } catch (err) {
        if (request !== fareRequest.current) return;
        // A fare filled in for the previous choices no longer applies; one the
        // user typed stays.
        if (fareIsAuto.current) {
          fareIsAuto.current = false;
          setV((x) => ({ ...x, fare: "" }));
          setFareBreakdown(null);
        }
        const fallback = "The fare service did not respond.";
        const reason = err?.response || err?.request ? extractErrorMessage(err, fallback) : (err?.message || fallback);
        const message = /manually/i.test(reason) ? reason : `${reason} You can type the fare in manually.`;
        setFareStatus({ state: "error", message });
      }
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, activeTab, v.enterpriseId, v.packageId, v.city, v.vehicleClass, isRoundTrip]);

  const applyPlanFare = () => {
    if (fareStatus.state !== "suggestion") return;
    fareIsAuto.current = true;
    setV((x) => ({ ...x, fare: String(fareStatus.total) }));
    setFareBreakdown(fareStatus.breakdown);
    setFareStatus({ state: "ok", message: "" });
    clearErrors("fare");
  };

  // Enterprise details pre-fill the passenger and the first active pricing
  // plan, but never over something the user typed or picked themselves.
  const fillUntouched = (x, suggestions) => {
    const next = { ...x };
    for (const [key, value] of Object.entries(suggestions)) {
      if (value == null || value === "") continue;
      if (x[key] && x[key] !== enterpriseFilled.current[key]) continue;
      next[key] = value;
      enterpriseFilled.current[key] = value;
    }
    return next;
  };

  const onEnterpriseChange = async (e) => {
    const enterpriseId = e.target.value;
    const found = ents.find((z) => z.value === enterpriseId);
    const request = ++enterpriseRequest.current;
    autoFare.current = true;
    clearErrors("enterpriseId");
    setV((x) => ({ ...x, enterpriseId, enterpriseName: found?.label || "" }));
    if (!enterpriseId) return;

    try {
      const body = await getEnterpriseById(enterpriseId);
      if (request !== enterpriseRequest.current) return;
      const ent = body?.data ?? body;
      if (!ent) return;

      const person = ent.authorisedPerson || {};
      const plans = ent.pricingPlans || [];
      const plan = plans.find((p) => p.isActive !== false) || plans[0];
      const planPackage = plan
        ? pkgs.find((p) => String(p.value) === String(plan.packageId)) || findOption(pkgs, plan.package, "code")
        : null;

      const suggestions = {
        paxName: person.name,
        paxPhone: person.phone,
        email: person.email,
        city: plan ? findCityOption(cities, plan.city)?.label : undefined,
        vehicleClass: plan ? (findOption(categories, plan.category)?.value || plan.category) : undefined,
        packageId: planPackage?.value,
        packageLabel: planPackage?.label,
      };
      setV((x) => fillUntouched(x, suggestions));
      clearErrors("paxName", "paxPhone", "email", "city", "vehicleClass", "packageId");
    } catch (err) {
      console.warn("Could not load enterprise details:", err);
    }
  };

  const onPackageChange = (e) => {
    const packageId = e.target.value;
    const found = pkgs.find((z) => z.value === packageId);
    autoFare.current = true;
    clearErrors("packageId");
    setV((x) => ({ ...x, packageId, packageLabel: found?.label || "" }));
  };

  const validate = () => {
    const errs = {};
    const text = (s) => String(s ?? "").trim();

    if (activeTab === "enterprise" && !v.enterpriseId) errs.enterpriseId = "Select the enterprise this booking is for";
    if (activeTab === "individual" && !v.paymentMethod) errs.paymentMethod = "Select how the customer will pay";

    const phone = normalizePhone(v.paxPhone);
    if (!phone) errs.paxPhone = "Enter the passenger's mobile number";
    else if (!/^\d{10}$/.test(phone)) errs.paxPhone = "Mobile number must be 10 digits";
    if (!text(v.paxName)) errs.paxName = "Enter the passenger's name";
    if (text(v.email) && !EMAIL_RX.test(text(v.email))) errs.email = "Enter a valid email address, e.g. name@company.com";
    if (text(v.mobile2) && !/^\d{10}$/.test(normalizePhone(v.mobile2))) errs.mobile2 = "Mobile 2 must be 10 digits";
    if (text(v.emailAlt) && !EMAIL_RX.test(text(v.emailAlt))) errs.emailAlt = "Enter a valid alternate email address";

    if (!v.city) errs.city = "Select the city";
    if (!v.dutyType) errs.dutyType = "Select the duty type: Rental, Transfer or Outstation";
    if (isOutstation && !v.outstationType) errs.outstationType = "Select One Way or Round Way";
    if (!v.vehicleClass) errs.vehicleClass = "Select the vehicle category to book";
    if (!v.packageId) errs.packageId = "Select a package";

    // An edit that keeps the saved pickup time is not blocked just because
    // that time has passed earlier today (a late pickup still gets edited).
    const scheduleChanged = !initial
      || v.carRequiredOn !== initial.carRequiredOn
      || v.carRequiredTime !== initial.carRequiredTime;
    if (!v.carRequiredOn) errs.carRequiredOn = "Select the pickup date";
    if (!v.carRequiredTime) errs.carRequiredTime = "Select the pickup time";
    if (v.carRequiredOn && v.carRequiredTime && !toPickupAtIso(v.carRequiredOn, v.carRequiredTime)) {
      errs.carRequiredOn = "The pickup date or time is not valid";
    } else if (v.carRequiredOn && v.carRequiredOn < localToday()) {
      errs.carRequiredOn = "The pickup date is in the past";
    } else if (scheduleChanged && isPickupInPast(v.carRequiredOn, v.carRequiredTime)) {
      errs.carRequiredTime = "This pickup time has already passed today";
    }

    if (v.pickupMode === "AIRPORT" && !text(v.pickupAirportName)) errs.pickupAirportName = "Enter the pickup airport or terminal";
    if (v.pickupMode === "POINT" && !text(v.pickupAddress)) errs.pickupAddress = "Enter the pickup address";
    if (v.dropMode === "AIRPORT" && !text(v.dropAirportName)) errs.dropAirportName = "Enter the drop airport or terminal";
    if (v.dropMode === "POINT" && !text(v.dropAddress)) errs.dropAddress = "Enter the drop address";

    const nonNegative = (x) => !isSet(x) || (Number.isFinite(Number(x)) && Number(x) >= 0);
    if (!nonNegative(v.distanceKm)) errs.distanceKm = "Distance must be 0 or more";
    if (!nonNegative(v.durationHr)) errs.durationHr = "Duration must be 0 or more";
    if (isSet(v.fare) && !(Number(v.fare) > 0)) errs.fare = "Fare must be more than ₹0, or left blank";

    return errs;
  };

  const focusField = (key) => {
    const el = key && rootRef.current?.querySelector(`[data-field="${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.querySelector("input, select, textarea")?.focus({ preventScroll: true });
  };

  const buildPayload = () => {
    const text = (s) => String(s ?? "").trim();
    return {
      isIndividual: activeTab === "individual",
      enterpriseId: activeTab === "enterprise" ? v.enterpriseId : "",
      enterpriseName: activeTab === "enterprise" ? v.enterpriseName : "Individual",
      paymentMethod: activeTab === "individual" ? v.paymentMethod : "",

      paxName: text(v.paxName),
      paxPhone: normalizePhone(v.paxPhone),
      email: text(v.email),
      gender: v.gender,

      mobile2: normalizePhone(v.mobile2),
      emailAlt: text(v.emailAlt),

      carRequiredOn: v.carRequiredOn,
      carRequiredTime: v.carRequiredTime,
      pickupAt: toPickupAtIso(v.carRequiredOn, v.carRequiredTime),

      city: v.city,
      // An outstation trip ends in another city; every other trip stays in the booking's city.
      dropCity: isOutstation ? (v.dropCity || v.city) : v.city,

      dutyType: v.dutyType,
      outstationType: isOutstation ? v.outstationType : "",

      pickupMode: v.pickupMode,
      pickupAirportName: text(v.pickupAirportName),
      pickupAddress: text(v.pickupAddress),
      pickupGoogleAddr: text(v.pickupGoogleAddr),
      pickupLat: v.pickupLat,
      pickupLng: v.pickupLng,
      pickupFlightNo: text(v.pickupFlightNo),

      dropMode: v.dropMode,
      dropAirportName: text(v.dropAirportName),
      dropAddress: text(v.dropAddress),
      dropGoogleAddr: text(v.dropGoogleAddr),
      dropLat: v.dropLat,
      dropLng: v.dropLng,
      dropFlightNo: text(v.dropFlightNo),

      terminateAt: text(v.terminateAt),
      packageId: v.packageId,
      packageLabel: v.packageLabel,
      vehicleClass: v.vehicleClass,
      notes: v.notes,
      fare: isSet(v.fare) ? Number(v.fare) : "",

      distanceKm: v.distanceKm,
      durationHr: v.durationHr,
    };
  };

  const save = async () => {
    if (submitting || readOnly) return;
    setServerError("");

    const errs = validate();
    setErrors(errs);
    const keys = orderedErrorKeys(errs);
    if (keys.length) {
      const action = initial ? "save the booking" : "create the booking";
      const names = keys.map((k) => FIELD_LABELS[k] || k);
      toast.error(
        keys.length === 1
          ? `Can't ${action}: ${errs[keys[0]]}.`
          : `Can't ${action}. ${keys.length} fields need attention: ${names.slice(0, 3).join(", ")}${keys.length > 3 ? ` and ${keys.length - 3} more` : ""}.`,
        TOAST_ERR
      );
      focusField(keys[0]);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(buildPayload());
    } catch (err) {
      console.error(initial ? "Error updating booking:" : "Error creating booking:", err);
      const message = extractErrorMessage(
        err,
        initial ? "The booking could not be saved. Please try again." : "The booking could not be created. Please try again.",
        {
          403: initial ? "You don't have permission to edit bookings." : "You don't have permission to create bookings.",
          404: "This booking no longer exists. It may have been deleted.",
        }
      );
      setServerError(message);
      const fieldErrs = serverFieldErrors(err?.response?.data?.errors, v);
      if (Object.keys(fieldErrs).length) {
        setErrors(fieldErrs);
        focusField(orderedErrorKeys(fieldErrs)[0]);
      }
      toast.error(message, TOAST_ERR);
    } finally {
      setSubmitting(false);
    }
  };

  const dutyTypes = useMemo(() => [
    { value: "RENTAL", label: "Rental (4h40km, 2h20km)" },
    { value: "TRANSFER", label: "Transfer (P2P, Airport to City, City to Airport)" },
    { value: "OUTSTATION", label: "Outstation (One Way, Round Way)" },
  ], []);

  /* ─── Progress computation ─── */
  const progressSteps = useMemo(() => {
    const steps = [
      {
        label: "Entity",
        done: activeTab === "individual" ? !!v.paymentMethod : !!v.enterpriseId,
      },
      {
        label: "Passenger",
        done: !!v.paxName?.trim() && !!v.paxPhone?.trim(),
      },
      {
        label: "Trip",
        done: !!v.city && !!v.dutyType && (!isOutstation || !!v.outstationType) && !!v.vehicleClass
          && !!v.carRequiredOn && !!v.carRequiredTime && !!v.packageId,
      },
      {
        label: "Pickup",
        done: v.pickupMode === "AIRPORT" ? !!v.pickupAirportName?.trim() : !!v.pickupAddress?.trim(),
      },
      {
        label: "Drop",
        done: v.dropMode === "AIRPORT" ? !!v.dropAirportName?.trim() : !!v.dropAddress?.trim(),
      },
      {
        label: "Pricing",
        done: !!v.fare && v.fare !== "" && v.fare !== 0,
      },
    ];
    return steps;
  }, [v, activeTab, isOutstation]);

  const completedCount = progressSteps.filter(s => s.done).length;
  const errorKeys = orderedErrorKeys(errors);

  return (
    <div ref={rootRef} className="bcf-root space-y-5 text-slate-900 dark:text-slate-100 p-1 md:p-0">
      {/* ─── Header ─── */}
      <div className="booking-form-card bcf-header-card" style={{ "--card-delay": "0ms" }}>
        {initial && (
          <div className="bcf-edit-banner">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>Editing existing booking{v.paxName ? ` for ${v.paxName}` : ""}</span>
          </div>
        )}
        <div className="booking-form-card-header">
          <div className={`booking-form-card-icon ${initial ? "bg-gradient-to-br from-indigo-100 to-violet-200 dark:from-indigo-900/40 dark:to-violet-800/40 text-indigo-600 dark:text-indigo-400" : "bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 text-emerald-600 dark:text-emerald-400"}`}>
            {readOnly ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            ) : initial ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              {readOnly ? "View Booking" : (initial ? "Edit Booking" : "Create Booking")}
            </h3>
            <p className="text-[12px] text-slate-500 dark:text-slate-400">
              {readOnly ? "View passenger, trip and pricing details" : "Capture passenger, trip and pricing details"}
            </p>
          </div>
          {!readOnly && (
            <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-sm">
                {completedCount}
              </div>
              <span>of {progressSteps.length} sections</span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {!readOnly && (
          <div className="px-5 py-3">
            <div className="booking-progress">
              {progressSteps.map((step, i) => (
                <div key={step.label} className="flex items-center flex-1 last:flex-none">
                  <div className="booking-progress-step">
                    <div className={`booking-progress-dot ${
                      step.done ? "booking-progress-dot-done" : "booking-progress-dot-pending"
                    }`}>
                      {step.done ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className={`hidden lg:block whitespace-nowrap ${step.done ? "text-emerald-600 dark:text-emerald-400 font-semibold" : "text-slate-400 dark:text-slate-500"}`}>
                      {step.label}
                    </span>
                  </div>
                  {i < progressSteps.length - 1 && (
                    <div className={`booking-progress-line ${step.done ? "booking-progress-line-done" : "booking-progress-line-pending"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Tab Switcher ─── */}
      {!readOnly && (
        <div className="booking-stagger flex items-center gap-4" style={{ "--stagger": "60ms" }}>
          <div className="booking-tab-bar">
            <TabButton
              active={activeTab === "enterprise"}
              onClick={() => switchTab("enterprise")}
              disabled={!!initial && activeTab !== "enterprise"}
            >
              Enterprise
            </TabButton>
            <TabButton
              active={activeTab === "individual"}
              onClick={() => switchTab("individual")}
              disabled={!!initial && activeTab !== "individual"}
            >
              Individual
            </TabButton>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {initial
              ? "The booking type can't be changed after the booking is created"
              : activeTab === "enterprise"
              ? "Book for an enterprise with standard fare plans"
              : "Book directly for a walk-in customer, priced from the B2C fare sheet"
            }
          </p>
        </div>
      )}

      {/* ─── Lists that failed to load ─── */}
      {!readOnly && lookupErrors.length > 0 && (
        <div role="alert" className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          <div className="font-medium flex items-center gap-2">
            <FiAlertCircle className="w-4 h-4 shrink-0" />
            Some dropdowns could not be loaded and may be empty
          </div>
          <ul className="mt-1 list-disc pl-6 text-xs space-y-0.5">
            {lookupErrors.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* ─── Booking Entity ─── */}
      {activeTab === "enterprise" && (
        <Card
          title="Booking Entity"
          subtitle="Select the enterprise this booking is for"
          icon={<FiBriefcase className="w-5 h-5" />}
          delay={100}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-field="enterpriseId">
              <Label required>Booking Entity Name</Label>
              <S value={v.enterpriseId} onChange={onEnterpriseChange} readOnly={readOnly} invalid={!!errors.enterpriseId}>
                <option value="">-- Select --</option>
                {ents.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </S>
              <FieldError msg={errors.enterpriseId} />
            </div>
            <div className="hidden md:block" />
          </div>
        </Card>
      )}

      {/* ─── Passenger ─── */}
      <PassengerSection
        v={v}
        set={set}
        errors={errors}
        readOnly={readOnly}
      />

      {/* ─── Travel Details ─── */}
      <AssignmentSection
        v={v}
        set={set}
        errors={errors}
        readOnly={readOnly}
        cityOptions={cityOptions}
        dutyTypes={dutyTypes}
        categoryOptions={categoryOptions}
        pkgs={pkgs}
        onPackageChange={onPackageChange}
      />

      {/* ─── Pickup ─── */}
      <PickupSection
        v={v}
        set={set}
        errors={errors}
        readOnly={readOnly}
        placeIssue={placeIssues.pickup}
        onModeChange={onModeChange}
        onPlaceTyping={onPlaceTyping}
        onPlaceSelect={onPlaceSelect}
      />

      {/* ─── Drop ─── */}
      <DropSection
        v={v}
        set={set}
        errors={errors}
        readOnly={readOnly}
        placeIssue={placeIssues.drop}
        onModeChange={onModeChange}
        onPlaceTyping={onPlaceTyping}
        onPlaceSelect={onPlaceSelect}
      />

      {/* ─── Pricing ─── */}
      <PricingSection
        v={v}
        setV={setV}
        set={set}
        errors={errors}
        readOnly={readOnly}
        activeTab={activeTab}
        fareBreakdown={fareBreakdown}
        fareStatus={fareStatus}
        onApplyPlanFare={applyPlanFare}
        distanceLoading={distanceLoading}
        distanceError={distanceError}
        routeEstimate={routeEstimate}
        isRoundTrip={isRoundTrip}
        onRecalculate={recalculateDistance}
        initial={initial}
        uploadDutySlip={uploadDutySlip}
        uploadParkingSlip={uploadParkingSlip}
        FileUploadComponent={FileUploadComponent}
      />

      {/* ─── What is stopping the save, and why ─── */}
      {!readOnly && (serverError || errorKeys.length > 0) && (
        <div role="alert" className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-4 text-rose-800 dark:text-rose-200">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <FiAlertCircle className="w-4 h-4 shrink-0" />
            {serverError
              ? (initial ? "The booking was not saved" : "The booking was not created")
              : `${errorKeys.length} field${errorKeys.length === 1 ? " needs" : "s need"} attention before the booking can be ${initial ? "saved" : "created"}`}
          </div>
          {serverError && <p className="mt-1 text-sm">{serverError}</p>}
          {errorKeys.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {errorKeys.map((k) => (
                <li key={k}>
                  <button type="button" className="text-left hover:underline" onClick={() => focusField(k)}>
                    <span className="font-medium">{FIELD_LABELS[k] || k}:</span> {errors[k]}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ─── Sticky Footer ─── */}
      <div className="booking-form-footer">
        <div className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
          {!readOnly && (
            <>
              <span className="font-medium text-emerald-600 dark:text-emerald-400">{completedCount}/{progressSteps.length}</span>
              {" "}sections completed
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn px-5"
            onClick={onCancel}
            disabled={submitting}
          >
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              className="booking-form-save-btn disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={save}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (initial ? "Saving..." : "Creating...") : initial ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              ) : "Create Booking"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileUploadComponent({ bookingId, fileType, onUpload, existingFile, readOnly = false }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);

    try {
      await onUpload(bookingId, file);
      toast.success(`${fileType === 'dutySlip' ? 'Duty slip' : 'Parking slip'} uploaded successfully`);
      setFile(null);

      const fileInput = document.getElementById(`${fileType}-input-create`);
      if (fileInput) fileInput.value = '';

      window.location.reload();
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      toast.error(error?.response?.data?.message || `Failed to upload ${fileType}`);
    } finally {
      setUploading(false);
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    const cleanPath = filePath.replace(/^uploads\//, '').replace(/^\/+/, '');
    const apiBaseUrl = process.env.REACT_APP_API_URL ||
                      (window.location.origin.includes(':3000')
                        ? window.location.origin.replace(':3000', ':5000')
                        : window.location.origin);
    return `${apiBaseUrl}/api/uploads/${cleanPath}`;
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  const isPdfFile = (filename) => {
    if (!filename) return false;
    return filename.toLowerCase().endsWith('.pdf');
  };

  const getFileName = (filePath) => {
    if (!filePath) return 'Unknown';
    return filePath.split('/').pop();
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const bgColor = fileType === 'dutySlip' ? 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20' : 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20';
  const buttonColor = fileType === 'dutySlip' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700';
  const textColor = fileType === 'dutySlip' ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300';
  const title = fileType === 'dutySlip' ? 'Duty Slip' : 'Parking Slip';

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="flex items-center gap-2">
          <input
            id={`${fileType}-input-create`}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileSelect}
            className="flex-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
            disabled={uploading}
          />
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="btn btn-primary text-sm px-3 py-2"
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      )}

      {existingFile && (
        <div className={`border rounded-lg p-4 bg-gradient-to-br ${bgColor}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</div>
            <button
              onClick={() => handleDownload(getFileUrl(existingFile), getFileName(existingFile))}
              className={`text-xs ${buttonColor} text-white px-3 py-1 rounded-md transition-colors flex items-center gap-1`}
              title={`Download ${title}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>

          {isImageFile(existingFile) ? (
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-2">
                <img
                  src={getFileUrl(existingFile)}
                  alt={title}
                  className="w-full h-48 rounded-lg object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <div className="text-center text-sm text-red-600 hidden">Failed to load image</div>
              </div>
              <a
                href={getFileUrl(existingFile)}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-xs ${textColor} hover:underline text-center`}
              >
                View Full Size
              </a>
            </div>
          ) : isPdfFile(existingFile) ? (
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-6 text-center">
                <svg className="w-16 h-16 mx-auto text-red-600 mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <div className="text-sm font-medium text-slate-700">PDF Document</div>
                <div className="text-xs text-slate-500 mt-1">{getFileName(existingFile)}</div>
              </div>
              <a
                href={getFileUrl(existingFile)}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-xs ${textColor} hover:underline text-center`}
              >
                Open PDF
              </a>
            </div>
          ) : (
            <a
              href={getFileUrl(existingFile)}
              target="_blank"
              rel="noopener noreferrer"
              className={`block border-2 border-dashed ${fileType === 'dutySlip' ? 'border-blue-300' : 'border-green-300'} rounded p-4 text-sm text-slate-700 hover:bg-${fileType === 'dutySlip' ? 'blue' : 'green'}-50 dark:hover:bg-${fileType === 'dutySlip' ? 'blue' : 'green'}-900/30 text-center transition-colors`}
            >
              <svg className="w-8 h-8 mx-auto mb-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="font-medium">View Document</div>
              <div className="text-xs text-slate-500 mt-1">{getFileName(existingFile)}</div>
            </a>
          )}
        </div>
      )}

      {!readOnly && file && (
        <div className="text-sm text-emerald-600 dark:text-emerald-400">
          <span className="font-medium">Selected:</span> {file.name}
        </div>
      )}
    </div>
  );
}
